import axios from "../util/axios";
import getCacheBuster from "@/util/getCacheBuster";
import { memoize } from "lodash";
import { createSelector } from "reselect";

const prefix = "features";

const FETCH_FEATURES_REQUEST = `${prefix}/FETCH_FEATURES_REQUEST`;
const FETCH_FEATURES_FAIL = `${prefix}/FETCH_FEATURES_FAIL`;
const FETCH_FEATURES_SUCCESS = `${prefix}/FETCH_FEATURES_SUCCESS`;

const FETCH_FEATURES_VALUES_REQUEST = `${prefix}/FETCH_FEATURES_VALUES_REQUEST`;
const FETCH_FEATURES_VALUES_FAIL = `${prefix}/FETCH_FEATURES_VALUES_FAIL`;
const FETCH_FEATURES_VALUES_SUCCESS = `${prefix}/FETCH_FEATURES_VALUES_SUCCESS`;

const initialState = {
  isFetching: false,
  list: null,
  values: {},
  isValuesFetching: {},
  error: null,
};

const fetchStart = () => ({ type: FETCH_FEATURES_REQUEST });
const fetchSuccess = (data) => ({ type: FETCH_FEATURES_SUCCESS, payload: data });
const fetchFail = (err) => ({ type: FETCH_FEATURES_FAIL, payload: err });

const fetchValuesStart = (id) => ({ type: FETCH_FEATURES_VALUES_REQUEST, payload: { id } });
const fetchValuesSuccess = (data) => ({ type: FETCH_FEATURES_VALUES_SUCCESS, payload: data });
const fetchValuesFail = (id, error) => ({ type: FETCH_FEATURES_VALUES_FAIL, payload: { id, error } });

// reducer

export default function materialReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_FEATURES_REQUEST:
      return {
        ...state,
        isFetching: true,
        error: null,
        list: null,
      };

    case FETCH_FEATURES_SUCCESS:
      return {
        ...state,
        data: action.payload,
        isFetching: false,
        error: null,
      };

    case FETCH_FEATURES_FAIL:
      return {
        ...state,
        isFetching: false,
        error: action.payload,
      };

    case FETCH_FEATURES_VALUES_REQUEST:
      return {
        ...state,
        isValuesFetching: {
          ...state.isValuesFetching,
          [action.payload.id]: true,
        },
      };

    case FETCH_FEATURES_VALUES_SUCCESS:
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.id]: action.payload.values,
        },
        isValuesFetching: {
          ...state.isValuesFetching,
          [action.payload.id]: false,
        },
      };

    case FETCH_FEATURES_VALUES_FAIL:
      return {
        ...state,
        isValuesFetching: {
          ...state.isValuesFetching,
          [action.payload.id]: false,
        },
      };

    default:
      return state;
  }
}

// selectors

const featuresRootSelector = (state) => state.features;

const featureValuesSelector = createSelector(featuresRootSelector, (root) => root.values);

const featureValuesIsFetchingSelector = createSelector(featuresRootSelector, (root) => root.isValuesFetching);

export const isFeaturesFetching = createSelector(featuresRootSelector, (featureData) => featureData.isFetching);

const isFeatureValuesFetching = createSelector(featureValuesIsFetchingSelector, (isValuesFetching) => memoize((id) => !!isValuesFetching[id]));

export const getFeatures = createSelector(featuresRootSelector, (featureData) => featureData.list);

export const getFeatureValues = createSelector(featureValuesSelector, (values) => memoize((id) => values[id]));

export function fetchFeatures() {
  return (dispatch, getState) => {
    const state = getState();
    const isFetching = isFeaturesFetching(state);
    const data = getFeatures(state);

    if (isFetching || data) {
      return Promise.resolve();
    }

    dispatch(fetchStart());

    return axios
      .get("api/features/", { params: { _: getCacheBuster() } })
      .then((res) => {
        dispatch(fetchSuccess(res.data));
      })
      .catch((err) => {
        dispatch(fetchFail(err));
      });
  };
}

export function fetchFeaturesValues(featureId) {
  return (dispatch, getState) => {
    const state = getState();
    const isFetching = isFeatureValuesFetching(state)(featureId);
    const data = getFeatureValues(state)(featureId);

    if (isFetching || data) {
      return Promise.resolve();
    }

    dispatch(fetchValuesStart(featureId));

    return axios
      .get(`api/features/${featureId}/`, { params: { _: getCacheBuster() } })
      .then((res) => {
        dispatch(fetchValuesSuccess(res.data));
      })
      .catch((err) => {
        dispatch(fetchValuesFail(featureId, err));
      });
  };
}
